import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  ArrowRight,
  Check,
  ChefHat,
  Sparkles,
  Zap,
  Wand2,
  Cookie,
  Coffee,
  Apple,
  Utensils,
  Loader2,
} from "lucide-react";
import { recipeApi, RECIPE_CATEGORIES } from "@/lib/recipeApi";

const RecipesLandingPage = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleUnlock = async (e) => {
    e?.preventDefault?.();
    setLoading(true);
    try {
      const { data } = await recipeApi.createCheckout(email.trim() || null);
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout — try again.");
      setLoading(false);
    }
  };

  return (
    <div className="bg-[#0A0A0A] min-h-screen text-white" data-testid="recipes-landing-page">
      {/* ============ FULL-WIDTH FOOD BANNER HERO ============ */}
      <section
        className="relative min-h-[100svh] flex items-center justify-center overflow-hidden pt-24"
        data-testid="fit-kitchen-hero"
      >
        {/* Background food image */}
        <div className="absolute inset-0">
          <picture>
            <source srcSet="/assets/images/fit-kitchen-hero.webp" type="image/webp" />
            <source srcSet="/assets/images/fit-kitchen-hero.jpg" type="image/jpeg" />
            <img
              src="/assets/images/fit-kitchen-hero.png"
              alt="High-protein recipes displayed on a luxury white marble kitchen island"
              className="w-full h-full object-cover"
              fetchPriority="high"
            />
          </picture>
          {/* Cinematic gradient overlays */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/50 to-black/85" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/25 to-black/40" />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
          className="relative z-10 w-full max-w-6xl mx-auto px-6 lg:px-12 text-center"
        >
          <div className="inline-flex items-center gap-2 border border-[#D4AF37]/60 bg-black/50 backdrop-blur-md px-5 py-2 mb-6">
            <Sparkles className="w-3.5 h-3.5 text-[#D4AF37]" />
            <span className="text-[#D4AF37] text-[10px] sm:text-xs tracking-[0.4em] uppercase font-bold">
              🍽️ Fit Kitchen · AI Recipe Generator
            </span>
          </div>

          <h1
            className="font-display text-[3.5rem] sm:text-7xl lg:text-8xl xl:text-[10rem] leading-[0.9] mb-4 tracking-tight text-white [text-shadow:0_4px_40px_rgba(0,0,0,0.7)]"
            style={{ fontFamily: "'Bebas Neue', sans-serif" }}
          >
            FIT <span className="text-[#D4AF37]">KITCHEN</span>
          </h1>
          <p
            className="font-display text-lg sm:text-2xl lg:text-3xl text-white/95 mb-3 tracking-[0.15em] uppercase [text-shadow:0_2px_20px_rgba(0,0,0,0.7)]"
            style={{ fontFamily: "'Bebas Neue', sans-serif" }}
          >
            AI Recipe Generator for Your Nutrition Program
          </p>
          <p className="text-white/85 text-base sm:text-lg lg:text-xl max-w-3xl mx-auto mb-8 leading-relaxed [text-shadow:0_2px_16px_rgba(0,0,0,0.7)]">
            <span className="text-[#D4AF37] font-semibold">250+ High-Protein Recipes</span> + Unlimited AI Recipe Generator
          </p>

          <div className="flex flex-wrap justify-center gap-2 mb-8 max-w-3xl mx-auto">
            {["Fat Loss", "Muscle Gain", "Wellness Athletes", "Bodybuilding", "Meal Prep", "Healthy Lifestyle"].map((tag) => (
              <span
                key={tag}
                className="text-[10px] sm:text-xs tracking-[0.15em] uppercase border border-[#D4AF37]/40 bg-black/40 backdrop-blur-sm text-[#D4AF37] px-3 py-1"
                data-testid={`audience-tag-${tag.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {tag}
              </span>
            ))}
          </div>

          <p className="text-white text-sm sm:text-base tracking-[0.2em] uppercase mb-3 [text-shadow:0_1px_10px_rgba(0,0,0,0.7)]">
            One-Time Payment · <span className="text-[#D4AF37] font-bold">$20 USD</span>
          </p>

          <Button
            onClick={handleUnlock}
            disabled={loading}
            className="bg-[#D4AF37] text-black hover:bg-white rounded-none px-10 sm:px-14 py-7 text-sm sm:text-base tracking-[0.28em] uppercase font-bold shadow-[0_20px_60px_-10px_rgba(212,175,55,0.6)] hover:shadow-[0_25px_70px_-8px_rgba(212,175,55,0.8)] transition-all duration-300"
            data-testid="unlock-btn"
          >
            {loading ? (
              <>
                <Loader2 size={18} className="mr-2 animate-spin" />
                Redirecting…
              </>
            ) : (
              <>
                🔥 Unlock Lifetime Access
                <ArrowRight size={18} className="ml-3" />
              </>
            )}
          </Button>

          <p className="text-center mt-5 text-white/70 text-xs sm:text-sm">
            No monthly subscription · Lifetime access · Already have access?{" "}
            <Link to="/recipes/login" className="text-[#D4AF37] hover:text-white transition-colors font-semibold" data-testid="login-link">
              Log in
            </Link>
          </p>
        </motion.div>
      </section>

      {/* ============ SECONDARY PRICING (kept for scroll-down conversion) ============ */}
      <section className="relative py-16 sm:py-20 border-y border-[#1A1A1A] bg-[#050505]">
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <motion.form
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            onSubmit={handleUnlock}
            className="max-w-xl mx-auto bg-black/60 backdrop-blur-sm border border-[#D4AF37]/40 p-6 sm:p-8 relative overflow-hidden text-center"
            data-testid="pricing-card"
          >
            <div className="absolute top-0 right-0 bg-[#D4AF37] text-black px-4 py-1.5 text-[10px] tracking-[0.3em] uppercase font-bold">
              One-Time
            </div>
            <p className="text-white/50 text-[10px] tracking-[0.35em] uppercase mb-2">
              Lifetime Access
            </p>
            <div className="flex items-baseline justify-center gap-2 mb-5">
              <span
                className="text-[#D4AF37] font-display text-6xl sm:text-7xl leading-none"
                style={{ fontFamily: "'Bebas Neue', sans-serif" }}
              >
                $20
              </span>
              <span className="text-white/50 text-sm tracking-wider uppercase">USD</span>
            </div>
            <p className="text-white/60 text-xs sm:text-sm mb-6">
              One-Time Payment · Lifetime Access · No Subscription
            </p>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com (optional — for receipt)"
              className="premium-input mb-4 rounded"
              autoComplete="email"
              data-testid="hero-email-input"
            />
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#D4AF37] text-black hover:bg-white rounded-none py-6 text-xs sm:text-sm tracking-[0.25em] uppercase font-bold shadow-[0_15px_40px_-10px_rgba(212,175,55,0.55)]"
              data-testid="pricing-unlock-btn"
            >
              {loading ? (
                <>
                  <Loader2 size={16} className="mr-2 animate-spin" />
                  Redirecting…
                </>
              ) : (
                <>
                  🔥 Unlock My Recipes
                  <ArrowRight size={16} className="ml-2" />
                </>
              )}
            </Button>
            <p className="text-center mt-4 text-white/40 text-[11px]">
              Already have access?{" "}
              <Link to="/recipes/login" className="text-[#D4AF37] hover:underline" data-testid="login-link-secondary">
                Log in
              </Link>
            </p>
          </motion.form>
        </div>
      </section>

      {/* ============ BENEFITS STRIP ============ */}
      <section className="border-t border-b border-[#1A1A1A] bg-black py-10 sm:py-14">
        <div className="max-w-5xl mx-auto px-6 lg:px-12 grid grid-cols-2 sm:grid-cols-5 gap-4 sm:gap-6">
          {[
            { icon: ChefHat, label: "100+ Recipes" },
            { icon: Wand2, label: "Unlimited AI" },
            { icon: Zap, label: "Make It Healthy" },
            { icon: Utensils, label: "Personalized Macros" },
            { icon: Sparkles, label: "Fat Loss · Muscle" },
          ].map((b, i) => (
            <div
              key={b.label}
              className="flex flex-col items-center text-center gap-2"
              data-testid={`benefit-${i}`}
            >
              <span className="w-10 h-10 border border-[#D4AF37]/40 bg-black flex items-center justify-center">
                <b.icon size={16} className="text-[#D4AF37]" />
              </span>
              <p className="text-[10px] sm:text-xs tracking-[0.2em] uppercase text-white/70 font-semibold leading-tight">
                {b.label}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* ============ HOW IT WORKS ============ */}
      <section className="py-20 sm:py-28">
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-14">
            <p className="text-[#D4AF37] text-[11px] tracking-[0.4em] uppercase mb-3">
              How It Works
            </p>
            <h2
              className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight"
              style={{ fontFamily: "'Bebas Neue', sans-serif" }}
            >
              THREE STEPS TO <span className="text-[#D4AF37]">SMARTER COOKING</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {[
              {
                n: "01",
                title: "Unlock",
                body: "Pay once ($20) and create your account. Lifetime access, no subscription.",
              },
              {
                n: "02",
                title: "Generate or Browse",
                body:
                  "Browse 100+ hand-crafted high-protein recipes or let AI create one tailored to your calories, macros, cuisine, and dietary needs.",
              },
              {
                n: "03",
                title: "Make It Healthy",
                body:
                  "Type any restaurant meal, dessert, or fast food — AI instantly recreates it as a macro-friendly version with fewer calories and more protein.",
              },
            ].map((s, i) => (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.6 }}
                className="border border-[#1A1A1A] hover:border-[#D4AF37]/40 bg-black/40 p-6 sm:p-8 transition-colors group"
                data-testid={`step-${i}`}
              >
                <p
                  className="text-[#D4AF37] font-display text-5xl mb-4 group-hover:scale-110 transition-transform origin-left"
                  style={{ fontFamily: "'Bebas Neue', sans-serif" }}
                >
                  {s.n}
                </p>
                <h3 className="text-white text-xl sm:text-2xl font-semibold mb-3 tracking-tight">
                  {s.title}
                </h3>
                <p className="text-white/60 text-sm leading-relaxed">{s.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ CATEGORY GRID ============ */}
      <section className="py-20 sm:py-28 bg-[#050505] border-t border-[#1A1A1A]">
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <p className="text-[#D4AF37] text-[11px] tracking-[0.4em] uppercase mb-3">
              What's Inside
            </p>
            <h2
              className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight mb-4"
              style={{ fontFamily: "'Bebas Neue', sans-serif" }}
            >
              EVERY CATEGORY <span className="text-[#D4AF37]">COVERED</span>
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
            {RECIPE_CATEGORIES.map((c, i) => (
              <div
                key={c.key}
                className="group border border-[#1A1A1A] hover:border-[#D4AF37]/50 bg-black/60 aspect-square flex flex-col items-center justify-center text-center p-3 sm:p-4 transition-all hover:-translate-y-1"
                data-testid={`category-tile-${c.key}`}
              >
                {CATEGORY_ICONS[c.key]}
                <p className="text-white text-xs sm:text-sm font-semibold mt-2 leading-tight">
                  {c.label}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-10 text-center">
            <p className="text-white/60 text-sm sm:text-base max-w-2xl mx-auto leading-relaxed">
              High-protein breakfasts · healthy desserts that taste sinful · full lunch &
              dinner catalog · post-workout drinks · portion-perfect meal prep · nine
              cuisines · plus unlimited AI custom recipes.
            </p>
          </div>
        </div>
      </section>

      {/* ============ MAKE IT HEALTHY ============ */}
      <section className="py-20 sm:py-28">
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div>
              <p className="text-[#D4AF37] text-[11px] tracking-[0.4em] uppercase mb-3">
                Signature Feature
              </p>
              <h2
                className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight mb-6"
                style={{ fontFamily: "'Bebas Neue', sans-serif" }}
              >
                TURN <span className="text-[#D4AF37]">ANY MEAL</span> INTO A
                MACRO-FRIENDLY VERSION
              </h2>
              <p className="text-white/70 text-base leading-relaxed mb-6">
                Craving a Big Mac, Cheesecake Factory pasta, or Starbucks Frappuccino?
                Type it in — our AI recreates it as a healthier, high-protein version
                with the same flavor profile.
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  "Complete macro breakdown vs the original",
                  "Estimated calorie savings + protein increase",
                  "Suggested ingredient swaps",
                  "Optional lower-calorie / higher-protein / meal-prep variants",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-3 text-white/80 text-sm">
                    <Check size={14} className="text-[#D4AF37] mt-1 flex-shrink-0" />
                    {t}
                  </li>
                ))}
              </ul>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {[
                "Big Mac",
                "Chick-fil-A Sandwich",
                "Starbucks Frappuccino",
                "Cinnabon Cinnamon Roll",
                "Cheesecake Factory Pasta",
                "Domino&rsquo;s Pizza",
                "Krispy Kreme Donut",
                "Chipotle Burrito",
              ].map((f) => (
                <div
                  key={f}
                  className="border border-[#1A1A1A] hover:border-[#D4AF37]/50 bg-black/60 p-4 text-center transition-colors"
                  data-testid={`healthy-example-${f.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  <p className="text-white text-sm font-medium">{f}</p>
                  <p className="text-[#D4AF37] text-[10px] tracking-wider uppercase mt-1">
                    → Healthy Version
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section className="py-20 sm:py-28 border-t border-[#1A1A1A] bg-[#050505]">
        <div className="max-w-3xl mx-auto px-6 lg:px-12 text-center">
          <h2
            className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight mb-6"
            style={{ fontFamily: "'Bebas Neue', sans-serif" }}
          >
            START COOKING <span className="text-[#D4AF37]">SMARTER TODAY</span>
          </h2>
          <p className="text-white/60 text-base mb-8">
            One-Time Payment: $20 USD · No subscription · Lifetime access
          </p>
          <Button
            onClick={handleUnlock}
            disabled={loading}
            className="bg-[#D4AF37] text-black hover:bg-white rounded-none px-10 py-6 text-xs sm:text-sm tracking-[0.25em] uppercase font-bold shadow-[0_15px_40px_-10px_rgba(212,175,55,0.55)]"
            data-testid="final-cta-btn"
          >
            {loading ? (
              <>
                <Loader2 size={16} className="mr-2 animate-spin" />
                Redirecting…
              </>
            ) : (
              <>
                🔥 Unlock My Recipes
                <ArrowRight size={16} className="ml-2" />
              </>
            )}
          </Button>
        </div>
      </section>
    </div>
  );
};

const CATEGORY_ICONS = {
  breakfast: <ChefHat size={26} className="text-[#D4AF37]" />,
  dessert: <Cookie size={26} className="text-[#D4AF37]" />,
  lunch_dinner: <Utensils size={26} className="text-[#D4AF37]" />,
  snack: <Apple size={26} className="text-[#D4AF37]" />,
  drink: <Coffee size={26} className="text-[#D4AF37]" />,
  meal_prep: <ChefHat size={26} className="text-[#D4AF37]" />,
};

export default RecipesLandingPage;
